<script>
import { mapGetters } from 'vuex';

import CategoriesSection from '@components/faq/faq-categories.vue';
import Layout from '@layouts/main.vue';

import Translatable from '@src/mixins/translatable.js';
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js';
import ImageMixin from '@src/mixins/imageMixin.js';
import Resizable from '@src/mixins/resizable.js';
import AuthorLabel from '@src/mixins/author.js';
import { sharedBlocksMixin } from '@src/mixins/sharedBlocksMixin.js';
import SeoMixin from '@src/mixins/seo.js';

import SharedBlockSlot from '@components/sharedBlocks/SharedBlockSlot.vue';

export default {
  /*
  page: {
    title: 'Faq',
    meta: [{ name: 'description', content: 'The Faq Page.' }],
  },
  */
  components: {
    Layout,
    CategoriesSection,
    SharedBlockSlot,
  },
  mixins: [
    Translatable,
    SanitizerMixin,
    ImageMixin,
    Resizable,
    AuthorLabel,
    sharedBlocksMixin,
    SeoMixin,
  ],
  provide() {
    const provided = {};
    Object.defineProperty(provided, 'sortedSharedBlocks', {
      enumerable: true,
      get: () => this.sortedSharedBlocks,
    });
    return {
      sharedBlocksProvider: provided,
    };
  },
  computed: {
    ...mapGetters('faq', ['pageData', 'dataFAQ']),

    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen ? this.pageData.mainImage.web : this.pageData.mainImage.app;
      }
      return null;
    },
    faqItems() {
      const lang = this.currentLanguage;
      const items = [];

      (this.dataFAQ || []).forEach((category) => {
        (category.data || []).forEach((faq) => {
          const content = faq.content?.[lang] || faq.content?.en;
          if (!content) return;

          items.push({
            question: this.stripHtml(content.title),
            answer: this.stripHtml(content.text),
          });
        });
      });

      return items;
    },

    seoEntity() {
      return {
        ...this.pageData,
        isStatic: true,
        faqItems: this.faqItems,
      };
    },
  },
  watch: {
    pageData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.slugs) {
          this.$store.commit('translations/SET_ALTERNATE_SLUGS', newVal.slugs);
        }
      },
    },
  },
  methods: {
    getSeoText(obj) {
      if (!obj) return '';
      return obj[this.currentLanguage] || obj.en || '';
    },
    stripHtml(value) {
      return (value || '').replace(/<[^>]*>?/gm, '').trim();
    },
  },
};
</script>

<template>
  <Layout>
    <section
      v-if="pageImg"
      class="header-section justify-center"
      :style="getBackground(pageImg.url)"
      :title="getSeoText(pageImg.title)"
      role="img"
      :aria-label="getSeoText(pageImg.alt)"
    ></section>
    <section class="description-banner-section justify-center">
      <div class="description-container">
        <p class="authorf container" v-html="authorLabel(pageData)"></p>
        <div class="description-header-container">
          <h1
            class="description-title"
            v-html="getTranslatedContent('title', pageData.content)"
          ></h1>
          <h3
            class="description-subtitle"
            v-html="getTranslatedContent('subtitle', pageData.content)"
          ></h3>
        </div>
        <div class="content-container">
          <CategoriesSection />
        </div>
      </div>
    </section>
    <SharedBlockSlot :slot-index="1" />
    <SharedBlockSlot :slot-index="2" />
    <SharedBlockSlot :slot-index="3" />
    <SharedBlockSlot :slot-index="4" />
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.header-section {
  height: 550px;
  width: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.description-banner-section {
  margin-top: -335px;

  & .description-container {
    width: 1140px;
    min-height: 800px;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
    padding: 15px;
  }
}

.description-header-container {
  width: 100%;
  min-height: 120px;
  height: auto;
  background: $dark-blue;
  padding: 10px 0 20px;
  border-radius: 10px;

  .description-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin: 0 22px;
    color: $white;
  }

  .description-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin: 0 25px;
    color: $white;
  }
}

.content-container {
  margin-top: 30px;
  width: 100%;
}

@media only screen and (max-width: $screen-width-xs) {
  .container {
    width: 100%;
  }

  .header-section {
    height: 244px;
  }

  .description-header-container {
    width: 100%;
    margin-top: 70px;
    margin-right: 0;
    min-height: 125px;
    height: auto;

    .description-title {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 20px 8px 10px;
      color: $white;
      text-align: center;
    }

    .description-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
      text-align: center;
    }
  }

  .description-banner-section {
    margin: -130px 0 40px;
    & .description-container {
      width: 100%;
      padding: 0;
    }
  }

  .description-header-container {
    margin-top: -20px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .content-container {
    width: 100%;
    margin-top: 20px;
  }
}
</style>
